import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import { useNavigate } from 'react-router-dom';
import {
  EmptyState, ListTable, Modal, Panel, SearchField, SegmentedControl, TilePicker, ToolbarButton, type ListColumn,
} from '../../../ui-kit';
import {
  extensionVersion, fetchCwOptions, handToExtension, startCwRun, type CwOptions, type CwReview,
} from '../../../services/carrierWebsiteService';
import { matches } from '../rocco/live';
import { useAgentAddress } from './address';

type Customer = CwOptions['customers'][number];

const REVIEW_KEY = 'klaris.cw.review';
const REVIEWS: { value: CwReview; label: string }[] = [
  { value: 'unsure', label: 'Only when unsure' },
  { value: 'every', label: 'Every screen' },
];

/**
 * Everyone we can quote, as a list. A row's button opens a window to pick the
 * carrier and how often to stop for a check, then hands the run to the Klaris
 * extension. Opened from the sales desk, the window opens for that lead.
 */
export default function NewRunPage() {
  const navigate = useNavigate();
  const base = useAgentAddress();
  const [options, setOptions] = React.useState<CwOptions | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');
  const [chosen, setChosen] = React.useState<string | null>(() => new URLSearchParams(window.location.search).get('customer'));

  React.useEffect(() => {
    fetchCwOptions().then(setOptions).catch((e: Error) => setError(e.message));
  }, []);

  const all = options?.customers ?? [];
  const rows = all.filter((c) => matches(query, [c.name, c.source, c.source_detail, c.state, c.current_carrier]));
  const who = all.find((c) => c.id === chosen) ?? null;

  const columns: ListColumn<Customer>[] = [
    {
      key: 'name', header: 'Customer', grow: 1, minWidth: 200, sortValue: (c) => c.name,
      render: (c) => <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{c.name}</Typography>,
    },
    {
      key: 'source', header: 'Lead source', grow: 1, minWidth: 200, sortValue: (c) => c.source,
      render: (c) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{c.source}</Typography>
          {c.source_detail ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{c.source_detail}</Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      key: 'drivers', header: 'Drivers', width: 100, sortValue: (c) => c.drivers,
      render: (c) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, fontVariantNumeric: 'tabular-nums' })}>{c.drivers}</Typography>
      ),
    },
    {
      key: 'vehicles', header: 'Vehicles', width: 100, sortValue: (c) => c.vehicles,
      render: (c) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, fontVariantNumeric: 'tabular-nums' })}>{c.vehicles}</Typography>
      ),
    },
    {
      key: 'state', header: 'State', width: 90, sortValue: (c) => c.state,
      render: (c) => <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{c.state ?? ''}</Typography>,
    },
    {
      key: 'current', header: 'Current insurer', width: 190, sortValue: (c) => c.current_carrier,
      render: (c) => (
        <Typography variant="body2" sx={(t) => ({ color: c.current_carrier ? t.tokens.text.secondary : t.tokens.text.tertiary })}>
          {c.current_carrier ?? 'Not insured'}
        </Typography>
      ),
    },
    {
      key: 'action', header: 'Action', width: 120, align: 'right',
      render: (c) => (
        <ToolbarButton variant="ghost" icon={<PlayArrowRounded />} onClick={() => setChosen(c.id)}>
          Quote
        </ToolbarButton>
      ),
    },
  ];

  if (error && !options) return <EmptyState message={error} />;
  return (
    <>
      <Panel title="Customers" subtitle="New website chat leads first, then files confirmed in Carrier Quoting. Pick one to quote it on a carrier's website"
        search={<SearchField value={query} onChange={setQuery} placeholder="Search customers…" size="small" />}>
        <ListTable columns={columns} rows={rows} getRowKey={(c) => c.id} pageSize={25} loading={!options}
          onRowClick={(c) => setChosen(c.id)}
          emptyMessage={all.length ? 'Nobody matches. Clear the search.'
            : 'No customers yet. A quote request from the website chat, or a file confirmed in Carrier Quoting, shows here.'} />
      </Panel>
      {who && options ? (
        <StartRun customer={who} carriers={options.carriers} onClose={() => setChosen(null)}
          onStarted={(id) => navigate(`${base}/runs/${id}`)} />
      ) : null}
    </>
  );
}

/** The window: which carrier, how often to stop, then open the website. */
function StartRun({ customer, carriers, onClose, onStarted }: {
  customer: Customer;
  carriers: CwOptions['carriers'];
  onClose: () => void;
  onStarted: (runId: string) => void;
}) {
  const [carrier, setCarrier] = React.useState<string | null>(carriers.length === 1 ? carriers[0].key : null);
  // Each person's own habit, kept in this browser: new staff tend to want every screen.
  const [review, setReview] = React.useState<CwReview>(() => (localStorage.getItem(REVIEW_KEY) as CwReview) || 'unsure');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const installed = extensionVersion();
  const where = carriers.find((c) => c.key === carrier);

  const pickReview = (value: CwReview) => {
    setReview(value);
    localStorage.setItem(REVIEW_KEY, value);
  };
  const start = async () => {
    if (!carrier) return;
    setBusy(true);
    setError(null);
    try {
      const started = await startCwRun(customer.id, carrier, review);
      await handToExtension(started);
      onStarted(started.run.id);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} size="md" title={`Quote ${customer.name}`}
      subtitle={[customer.source, customer.source_detail, customer.state, `${customer.drivers} driver${customer.drivers === 1 ? '' : 's'}`,
        `${customer.vehicles} vehicle${customer.vehicles === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
      footer={(
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <ToolbarButton variant="ghost" onClick={onClose} disabled={busy}>Cancel</ToolbarButton>
          <ToolbarButton variant="brand" icon={<PlayArrowRounded />} onClick={start} loading={busy} loadingLabel="Opening"
            disabled={!carrier || !installed}>
            Open the website
          </ToolbarButton>
        </Stack>
      )}>
      <Stack spacing={2.5}>
        <Stack spacing={1}>
          <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>Carrier</Typography>
          <TilePicker ariaLabel="Carrier" columns={3} value={carrier} onChange={setCarrier}
            options={carriers.map((c) => ({
              value: c.key, title: c.name,
              description: c.guide === 'approved' ? 'We know this website' : 'First visit: stay and watch',
              meta: c.guide === 'approved' && c.version ? `Guide version ${c.version}` : 'No approved guide yet',
            }))} />
        </Stack>

        <Stack spacing={1}>
          <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>Stop for me to check</Typography>
          <Box sx={{ alignSelf: 'flex-start' }}>
            <SegmentedControl value={review} options={REVIEWS} onChange={pickReview} ariaLabel="When to stop for a check" />
          </Box>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
            {review === 'unsure'
              ? 'Moves on by itself when the guide filled a screen and the page agreed. Stops on anything it worked out, and always before a price.'
              : 'Stops on every screen for you to look over before it moves on.'}
          </Typography>
        </Stack>

        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
          The website opens in a new tab in this browser. If it asks for something the customer's file does not have,
          we highlight it on the page and wait for you.
        </Typography>

        {where && where.guide !== 'approved' ? (
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.semantic.warning })}>
            First visit to {where.name}: please stay and watch. We draft a guide for you to approve.
          </Typography>
        ) : null}
        {!installed ? (
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.semantic.warning })}>
            The Klaris extension is not in this browser. Open Integrations to add it, then reload this page.
          </Typography>
        ) : null}
        {error ? <Typography variant="body2" sx={(t) => ({ color: t.tokens.semantic.danger })}>{error}</Typography> : null}
      </Stack>
    </Modal>
  );
}
